import type { ResourceListOptions } from '#dn-types/api.ts';
import type { Domain, DomainListItem } from '#dn-types/domain.ts';
import { rewordValidationMessages } from './errors.ts';
import { del, get, getList, post } from './fetch.ts';

export const DEFAULT_DOMAINS_PAGE_SIZE = 500;

// GET /domains
export type GetDomainsResult = DomainListItem[];
type GetDomainsVariables = ResourceListOptions;
export function getDomains({ cursor, pageSize = DEFAULT_DOMAINS_PAGE_SIZE, init }: GetDomainsVariables = {}) {
  const params = new URLSearchParams({
    includeCounts: 'true',
    pageSize: pageSize.toString(),
  });

  if (cursor) {
    params.append('cursor', cursor);
  }

  return getList<GetDomainsResult>(`/v1/domains?${params.toString()}`, init);
}

// GET /domains/:id
export type GetDomainResult = Domain;
export function getDomain(id: string, init: RequestInit) {
  return get<GetDomainResult>(`/v1/domains/${encodeURIComponent(id)}`, init);
}

// POST /domains
export type AddDomainVariables = { domainName: string };
export type AddDomainResult = Domain;
export function addDomain(formData: AddDomainVariables) {
  return (
    post<AddDomainResult>('/v1/domains', formData)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'domainName' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'This domain already exists.';
          }
          if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
            if (typeof details.extra?.limit === 'number') {
              return `You have reached the limit of ${details.extra.limit} domains. Delete some unused domains before adding more, or contact support.`;
            }
            return 'You have the maximum number of domains allowed. Delete some unused domains before adding more, or contact support.';
          }
        });
      })
  );
}

// DELETE /domains/:name
export type DeleteDomainResult = Record<string, never>;
export function deleteDomain(id: string) {
  return (
    del<DeleteDomainResult>(`/v1/domains/${encodeURIComponent(id)}`)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === undefined && details.code === 'ERR_HAS_DEPENDENTS') {
            return 'There are identity providers that use this domain, please remove them first';
          }
        });
      })
  );
}

// POST /domains/:id/verify
export type VerifyDomainResult = Record<string, never>;
export function verifyDomain(domainId: string) {
  return (
    post<VerifyDomainResult>(`/v1/domains/${encodeURIComponent(domainId)}/verify`)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (
            details.path === undefined &&
            (details.code === 'ERR_DOMAIN_VERIFICATION_FAILED_NXDOMAIN' ||
              details.code === 'ERR_DOMAIN_VERIFICATION_FAILED_NO_RECORDS')
          ) {
            return 'We couldn’t verify your domain yet. It may take time for DNS changes to propagate.';
          }
          if (details.path === undefined && details.code === 'ERR_DOMAIN_VERIFICATION_FAILURE_INCORRECT') {
            return 'We couldn’t verify your domain. Please double-check the value in the TXT record.';
          }
          if (details.path === undefined && details.code === 'ERR_DOMAIN_VERIFICATION_FAILURE_IN_USE') {
            return 'This domain is already verified by another organization. Please contact Defined Networking support.';
          }
        });
      })
  );
}
